/**
 * trends: how a chart point of each widget maps back to the rows behind it,
 * for the conversations drill-down. Same filtering as the widget's loader.
 *
 * `top-hashtags` has no drill — the prototype's table raises none.
 */
import type { DrillRef } from '@/data-sources/contract';
import { bucketStart } from '@/lib/analytics/buckets';
import { isoToMs } from '@/lib/analytics/dates';

import type { MentionRow } from '../engine/generator';
import { scoped } from '../query';
import type { DrillResolver, DrillResolvers } from './types';

/** A keyword's rows — `pivot(...).rowsAt(i)` / the frequency card's `row.keyword === hit.keyword`. */
const byKeyword: DrillResolver = (ref, query) => {
  const rows = scoped(query).own;
  const keyword = ref.category?.value;
  return keyword === undefined ? rows : rows.filter((row) => row.keyword === keyword);
};

const byTopic: DrillResolver = (ref, query) => {
  const rows = scoped(query).own;
  const topic = ref.category?.value;
  return topic === undefined ? rows : rows.filter((row) => row.topic === topic);
};

/** A day on the spikes plot opens the whole day — every topic, as `aggregate().rowsAt` does. */
const byDay = (ref: DrillRef, rows: readonly MentionRow[]) => {
  const bucket = ref.bucket;
  if (!bucket) return rows;
  const start = isoToMs(bucket.start);
  return rows.filter((row) => bucketStart(row.day, bucket.unit) === start);
};

export const trendsDrillResolvers: DrillResolvers = {
  'trending-keywords': byKeyword,
  'keyword-sentiment': byKeyword,
  'keyword-mentions-frequency': byKeyword,
  'trending-topics': byTopic,
  'engagement-spikes': (ref, query) => byDay(ref, scoped(query).own),
};
